/** The parent portal's menu, page titles and which pages are about one child. */

export interface GuardianMenuItem {
  routeName: string;
  menuText: string;
  menuIcon: string;
  tooltipText: string;
  path: string;
}

const item = (routeName: string, menuText: string, menuIcon: string, path: string): GuardianMenuItem =>
  ({ routeName, menuText, menuIcon, tooltipText: menuText, path });

const tertiaryItems = (): GuardianMenuItem[] => [
  item('parent-courses', 'Courses', 'pi-book', '/parent/courses'),
  item('parent-transcripts', 'Transcripts', 'pi-file', '/parent/transcripts'),
];

/**
 * Courses and transcripts exist only in tertiary schools. Messages is the
 * school chat, which needs the chat permission (staff who are also parents).
 */
export function guardianMenuItems(isTertiary: boolean, canChat = false): GuardianMenuItem[] {
  return [
    item('parent-home', 'Home', 'pi-home', '/parent/home'),
    item('parent-results', 'Results', 'pi-chart-bar', '/parent/results'),
    item('parent-attendance', 'Attendance', 'pi-calendar', '/parent/attendance'),
    item('parent-timetable', 'Timetable', 'pi-clock', '/parent/timetable'),
    ...(isTertiary ? tertiaryItems() : []),
    item('parent-bills', 'Bills & Payments', 'pi-wallet', '/parent/bills'),
    item('parent-announcements', 'Announcements', 'pi-megaphone', '/parent/announcements'),
    ...(canChat ? [item('parent-messages', 'Messages', 'pi-comments', '/parent/messages')] : []),
    item('parent-tickets', 'Support', 'pi-ticket', '/parent/tickets'),
    item('parent-settings', 'Settings', 'pi-cog', '/parent/settings'),
  ];
}

export const GUARDIAN_PAGE_TITLES: Readonly<Record<string, string>> = {
  'parent-home': 'Overview',
  'parent-results': 'Results',
  'parent-attendance': 'Attendance',
  'parent-timetable': 'Timetable',
  'parent-bills': 'Bills & Payments',
  'parent-bill-detail': 'Bill Detail',
  'parent-payment-status': 'Payment Status',
  'parent-courses': 'Course Registration',
  'parent-transcripts': 'Transcript Requests',
  'parent-announcements': 'Announcements',
  'parent-messages': 'Messages',
  'parent-tickets': 'Support',
  'parent-ticket-detail': 'Ticket Detail',
  'parent-profile': 'My Profile',
  'parent-settings': 'Settings',
};

export const guardianPageTitle = (routeName: unknown): string =>
  (typeof routeName === 'string' && GUARDIAN_PAGE_TITLES[routeName]) || 'Parent Portal';

const CHILD_SPECIFIC_ROUTES = new Set([
  'parent-results', 'parent-attendance', 'parent-timetable',
  'parent-courses', 'parent-transcripts',
  'parent-bills', 'parent-bill-detail', 'parent-payment-status',
  'parent-announcements',
]);

/** Pages that show one child's records, so the child banner + switcher belongs on them. */
export const isChildSpecificRoute = (routeName: unknown): boolean =>
  typeof routeName === 'string' && CHILD_SPECIFIC_ROUTES.has(routeName);

/** Exact match, or a page below the menu item ('/parent/bills/…' keeps Bills active). */
export const isActiveMenuPath = (currentPath: string, itemPath: string): boolean =>
  currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);

export function nameInitials(name: string | null | undefined, fallback = 'PG'): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
  return (parts[0]?.slice(0, 2) || fallback).toUpperCase();
}
